// src/components/header/headerNav.tsx
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { headerNavData, type NavItem } from "../../data/headerData";
import ImageWithBasePath from "../image-with-base-path";
import { all_routes } from "@/routes/all_routes";

const HeaderNav = () => {
  const location = useLocation();
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const [isMobileNav, setIsMobileNav] = useState<boolean>(
    () => typeof window !== "undefined" && window.innerWidth < 992
  );

  // Watch for viewport changes so we only run the submenu toggle on mobile
  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth < 992;
      setIsMobileNav(nextIsMobile);
      if (!nextIsMobile) setOpenSubmenuId(null);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Set active menu based on current route
  const isActive = (item: NavItem): string => {
    const itemPath = item.path;

    // Check if current path matches the item path exactly
    if (itemPath && location.pathname === itemPath) {
      return "active";
    }

    // Treat nested/relative routes as active (e.g. /appointments/123 keeps Appointments active)
    if (itemPath && location.pathname.startsWith(`${itemPath}/`)) {
      return "active";
    }

    // Check relative links - if current path matches any relative link, mark as active
    if (item.relativeLinks && item.relativeLinks.includes(location.pathname)) {
      return "active";
    }

    if (item.children) {
      return item.children.some((child) => isActive(child)) ? "active" : "";
    }
    return "";
  };

  const renderLinkElement = (
    item: NavItem,
    className?: string,
    children?: ReactNode,
    disableActive?: boolean
  ) => {
    const baseClass = [className, disableActive ? "" : isActive(item)]
      .filter(Boolean)
      .join(" ");

    if (item.target === "_blank") {
      return (
        <a
          href={item.path}
          className={baseClass || undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children ?? item.title}
        </a>
      );
    }

    return (
      <Link to={item.path} className={baseClass || undefined}>
        {children ?? item.title}
      </Link>
    );
  };

  // Render navigation items recursively
  const renderNavItems = (items: NavItem[]) => {
    return items.map((item) => {
      const hasChildren = item.children && item.children.length > 0;
      const isMegaMenu = hasChildren && item.isMegaMenu;
      const isOpen = openSubmenuId === item.id;

      const itemClass = [
        hasChildren ? "has-submenu" : "",
        isMegaMenu ? "megamenu" : "",
        isActive(item),
        item.className || "",
      ]
        .filter(Boolean)
        .join(" ");

      const mainLinkClass = [
        "main-menu",
        isActive(item),
        isOpen && isMobileNav ? "submenu" : "",
      ]
        .filter(Boolean)
        .join(" ");

      const handleParentClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
      ) => {
        if (!hasChildren) return;
        // Only toggle submenu on mobile; desktop should stay hover-based
        if (!isMobileNav) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        setOpenSubmenuId((prev) => (prev === item.id ? null : item.id));
      };

      return (
        <li key={item.id} className={itemClass}>
          {hasChildren ? (
            <>
              <Link
                to="#"
                className={mainLinkClass}
                onClick={handleParentClick}
              >
                {item.title}
                <span>
                  <i
                    className={`${
                      location.pathname === all_routes.home5
                        ? "fa-solid fa-plus"
                        : location.pathname === all_routes.home6
                        ? "fa-solid fa-plus"
                        : location.pathname === all_routes.home15
                        ? "isax isax-add-circle5"
                        : location.pathname === all_routes.home18 ?
                        "isax isax-add-circle5"
                        : "fa-solid fa-chevron-down "
                    }`}
                  ></i>
                </span>
              </Link>
              {isMegaMenu
                ? renderMegaMenu(item, isOpen)
                : renderDropdownMenu(item, isOpen)}
            </>
          ) : (
            renderLinkElement(item, mainLinkClass, item.title, true)
          )}
        </li>
      );
    });
  };

  // Render mega menu items
  const renderMegaMenu = (item: NavItem, isOpen: boolean) => {
    if (!item.children) return null;

    // Special handling for Pages mega menu
    if (item.id === "pages") {
      return (
        <ul
          className="submenu mega-submenu"
          style={isMobileNav ? { display: isOpen ? "block" : "none" } : {}}
        >
          <li>
            <div className="megamenu-wrapper megamenu-wrapper-one">
              <div className="row">
                {/* Column 1 */}
                <div className="col-lg-3 sub-menu-left">
                  <ul className="sub-menu-list">
                    {item.children.slice(0, 8).map((child) => (
                      <li key={child.id} className={isActive(child)}>
                        {renderLinkElement(child)}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Column 2 */}
                <div className="col-lg-3 sub-menu-left">
                  <ul className="sub-menu-list">
                    {item.children.slice(8, 16).map((child) => (
                      <li key={child.id} className={isActive(child)}>
                        {renderLinkElement(child)}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Column 3 */}
                <div className="col-lg-3 sub-menu-left">
                  <ul className="sub-menu-list">
                    {item.children.slice(16, 23).map((child) => (
                      <li key={child.id} className={isActive(child)}>
                        {renderLinkElement(child)}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Column 4 */}
                <div className="col-lg-3">
                  <ul className="sub-menu-list">
                    {item.children.slice(23).map((child) => (
                      <li key={child.id} className={isActive(child)}>
                        {renderLinkElement(child)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </li>
        </ul>
      );
    }

    // Default mega menu for other items (like Home)
    return (
      <ul
        className="submenu mega-submenu"
        style={isMobileNav ? { display: isOpen ? "block" : "none" } : {}}
      >
        <li>
          <div className="megamenu-wrapper">
            <div className="row">
              {item.children.map((child) => (
                <div className="col" key={child.id}>
                  <div className={`single-demo ${isActive(child)}`}>
                    {child.icon && (
                      <div className="demo-img">
                        {child.badge ? (
                          <span className="new-badge">New</span>
                        ): <></>}
                        {renderLinkElement(
                          child,
                          "inner-demo-img",
                          <ImageWithBasePath
                            src={child.icon}
                            className="img-fluid"
                            alt={child.title}
                          />,
                          true
                        )}
                      </div>
                    )}
                    <div className="demo-info">
                      {renderLinkElement(child, "inner-demo-img")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </li>
      </ul>
    );
  };

  // Render regular dropdown menu with layout variations per menu id
  const renderDropdownMenu = (item: NavItem, isOpen: boolean) => {
    const children = item.children ?? [];
    const style = isMobileNav ? { display: isOpen ? "block" : "none" } : {};

    if (item.id === "doctors") {
      return (
        <ul className="submenu sub-menu-one" style={style}>
          <li>
            <div className="row">
              <div className="col-lg-6">
                <ul className="sub-menu-list">
                  {children.map((child) => (
                    <li key={child.id} className={isActive(child)}>
                      {renderLinkElement(child)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="menu-img">
                  <ImageWithBasePath
                    src="assets/img/home/menu-img-1.jpg"
                    alt="listing"
                    className="img-fluid"
                  />
                </div>
              </div>
            </div>
          </li>
        </ul>
      );
    }

    if (item.id === "patients") {
      const firstColumn = children.slice(0, 8);
      const secondColumn = children.slice(8);

      return (
        <ul className="submenu sub-menu-one sub-menu-two" style={style}>
          <li>
            <div className="row">
              <div className="col-lg-4 sub-menu-left">
                <ul className="sub-menu-list">
                  {firstColumn.map((child) => (
                    <li key={child.id} className={isActive(child)}>
                      {renderLinkElement(child)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-4">
                <ul className="sub-menu-list">
                  {secondColumn.map((child) => (
                    <li key={child.id} className={isActive(child)}>
                      {renderLinkElement(child)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-4">
                <div className="menu-img">
                  <ImageWithBasePath
                    src="assets/img/home/menu-img-2.jpg"
                    alt="listing"
                    className="img-fluid"
                  />
                </div>
              </div>
            </div>
          </li>
        </ul>
      );
    }

    if (item.id === "pharmacy") {
      return (
        <ul className="submenu sub-menu-one sub-menu-three" style={style}>
          <li>
            <div className="row">
              <div className="col-lg-6">
                <ul className="sub-menu-list">
                  {children.map((child) => (
                    <li key={child.id} className={isActive(child)}>
                      {renderLinkElement(child)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-lg-6">
                <div className="menu-img">
                  <ImageWithBasePath
                    src="assets/img/home/menu-img-3.jpg"
                    alt="listing"
                    className="img-fluid"
                  />
                </div>
              </div>
            </div>
          </li>
        </ul>
      );
    }

    if (item.id === "blog" || item.id === "admin") {
      return (
        <ul className="submenu sub-menu-one sub-menu-default" style={style}>
          {children.map((child) => (
            <li key={child.id} className={isActive(child)}>
              {renderLinkElement(child)}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <ul className="submenu" style={style}>
        {children.map((child) => (
          <li key={child.id} className={isActive(child)}>
            {renderLinkElement(child)}
          </li>
        ))}
      </ul>
    );
  };

  // Filter out blog and admin menus when on home11 (index-11)
  const filteredNavData = headerNavData.filter((item) => {
    if (location.pathname === all_routes.home11) {
      return item.id !== "blog" && item.id !== "admin";
    }
    return true;
  });

  return <ul className="main-nav">{renderNavItems(filteredNavData)}</ul>;
};

export default HeaderNav;
